import { T } from "@repo/transifex";
import { ApiErrorCodesEnum, isApiError } from "~/api/error";
import { DisplayErrorCodeEnum, isDisplayError } from "~/utils/display-error";

type DisplayErrorMessageProps = {
  error: unknown;
  fallback?: React.ReactNode;
};

export const DisplayErrorMessage = ({
  error,
  fallback
}: DisplayErrorMessageProps) => {
  if (isDisplayError(error)) {
    switch (error.code) {
      case DisplayErrorCodeEnum.CARD_HEX_NOT_VALID:
        return <T _str="Card HEX is not valid" />;
      case DisplayErrorCodeEnum.PROFILE_NOT_SELECTED:
        return <T _str="Profile is not selected" />;
      case DisplayErrorCodeEnum.SESSION_NOT_ACTIVE:
        return <T _str="Session is not active" />;
      case DisplayErrorCodeEnum.SCANNER_INVALID_CODE:
        return <T _str="Invalid code scanned" />;
      case DisplayErrorCodeEnum.POI_USER_CANCEL:
        return <T _str="User cancelled the payment" />;
      case DisplayErrorCodeEnum.POI_TIMEOUT:
        return <T _str="Payment timed out. No card was presented." />;
      case DisplayErrorCodeEnum.CARD_ORDER_NOT_SENT:
        return <T _str="Connection lost. Nothing was charged, try again." />;
      case DisplayErrorCodeEnum.CARD_ORDER_TIMED_OUT:
        return (
          <T _str="Connection lost and the payment did not complete. Check Transactions before charging again." />
        );
    }
  }

  if (isApiError(error)) {
    switch (error.body.code) {
      case ApiErrorCodesEnum.CHILD_NOT_FOUND:
        return <T _str="User not found" />;
      case ApiErrorCodesEnum.CHILD_MULTIPLE_FOUND:
        return <T _str="Multiple users found" />;
      case ApiErrorCodesEnum.CHILD_IN_WRONG_SALESPLACE:
        return <T _str="QR code or card belongs to a different salesplace" />;
      case ApiErrorCodesEnum.OFFER_NOT_FOUND:
        return <T _str="Offer not found" />;
      case ApiErrorCodesEnum.OFFER_PRODUCTS_NOT_AVAILABLE:
        return <T _str="Products not available" />;
      case ApiErrorCodesEnum.ORDER_VALIDATION_HB_ALLOWANCE_EXCEEDED:
        return <T _str="Allowance exceeded" />;
      case ApiErrorCodesEnum.BAD_REQUEST:
        return <T _str="Invalid request" />;
      case ApiErrorCodesEnum.NOT_FOUND:
        return <T _str="Resource not found" />;
      case ApiErrorCodesEnum.NOT_ACCEPTABLE:
        return <T _str="Request not acceptable" />;
      case ApiErrorCodesEnum.UNAUTHORIZED:
        return <T _str="Authorization required" />;
      case ApiErrorCodesEnum.FORBIDDEN:
        return <T _str="Access denied" />;
      case ApiErrorCodesEnum.CONFLICT:
        return <T _str="Request conflicts with current state" />;
      case ApiErrorCodesEnum.UNPROCESSABLE_ENTITY:
        return <T _str="Request cannot be processed" />;
      case ApiErrorCodesEnum.INTERNAL_SERVER_ERROR:
        return <T _str="Server error occurred" />;
      case ApiErrorCodesEnum.PAYMENT_METHOD_NOT_ALLOWED:
        return <T _str="Payment method not allowed" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_ABORTED:
        return <T _str="Payment was aborted" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_BUSY:
        return <T _str="Payment terminal is busy" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_CANCELLED:
        return <T _str="Payment was cancelled" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_DEVICE_OUT:
        return <T _str="Payment device disconnected" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_IN_PROGRESS:
        return <T _str="Payment is in progress" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_INSERTED_CARD:
        return <T _str="Card already inserted" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_INVALID_CARD:
        return <T _str="Invalid payment card" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_LOGGED_OUT:
        return <T _str="Payment terminal logged out" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_MESSAGE_FORMAT:
        return <T _str="Invalid payment message format" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_NOT_ALLOWED:
        return <T _str="Payment operation not allowed" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_NOT_FOUND:
        return <T _str="Payment not found" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_PAYMENT_RESTRICTION:
        return <T _str="Payment restricted" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_REFUSAL:
        return <T _str="Payment was refused" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_UNAVAILABLE_DEVICE:
        return <T _str="Payment device unavailable" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_UNAVAILABLE_SERVICE:
        return <T _str="Payment service unavailable" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_UNREACHABLE_HOST:
        return <T _str="Payment service unreachable" />;
      case ApiErrorCodesEnum.PAYMENT_ADYEN_WRONG_PIN:
        return <T _str="Incorrect PIN entered" />;
      case ApiErrorCodesEnum.ORDER_ALREADY_PAID:
        return <T _str="Order has already been paid" />;
      case ApiErrorCodesEnum.ORDER_PAYMENT_IN_PROGRESS:
        return <T _str="A card payment for this order is still in progress" />;
      case ApiErrorCodesEnum.ORDER_VALIDATION_MINIMUM_AMOUNT_NOT_REACHED:
        return <T _str="Minimum quantity not reached" />;
      case ApiErrorCodesEnum.ORDER_VALIDATION_DEPOSIT_MISMATCH:
        return (
          <T _str="The deposit could not be confirmed. Contact support." />
        );
      case ApiErrorCodesEnum.USER_NOT_FOUND:
        return <T _str="User not found" />;
      case ApiErrorCodesEnum.POS_USER_NOT_CONFIGURED:
        return (
          <T _str="This till is not set up correctly. Contact Kanpla support." />
        );
      case ApiErrorCodesEnum.PAYMENT_USER_BALANCE_INSUFFICIENT:
        return <T _str="Insufficient balance" />;
      case ApiErrorCodesEnum.USER_LIMIT_OVER:
        return <T _str="Limit reached" />;
      case ApiErrorCodesEnum.COLLECT_FAILED_TO_UPDATE_ORDER:
        return (
          <T _str="Something went wrong while updating the order. Try again." />
        );
      case ApiErrorCodesEnum.COLLECT_INVALID_COLLECTION_EVENT:
        return (
          <T _str="Collection item is either unavailable or does not exist." />
        );
      case ApiErrorCodesEnum.COLLECT_PRODUCT_QUANTITY_EXCEEDED:
        return (
          <T _str="You're trying to collect more items than allowed. Adjust the quantity and try again." />
        );
      case ApiErrorCodesEnum.PRODUCT_COLORS_UNAVAILABLE:
        return (
          <T _str="Product colors are not set up on this server. Contact Kanpla support - trying again won't help." />
        );
      case ApiErrorCodesEnum.PRODUCT_COLORS_GENERATION_FAILED:
        return (
          <T _str="We couldn't pick colors for these products. Try again." />
        );
      case ApiErrorCodesEnum.PRODUCT_COLORS_STORAGE_FAILED:
        return (
          <T _str="The colors were picked but couldn't be saved. Try again." />
        );
      case ApiErrorCodesEnum.SUPPLIER_NOT_FOUND:
        return (
          <T _str="Please create a Branding for this location in the Admin Panel under Settings → Branding" />
        );
      case ApiErrorCodesEnum.COLLECT_DUPLICATE_MODULES_IN_BASKET:
        return (
          <T _str="You're trying to add two products from the same module" />
        );
      case ApiErrorCodesEnum.CARD_INVALID:
        return <T _str="Card identifier is not valid" />;
      case ApiErrorCodesEnum.CHILD_CARD_IN_USE:
        return <T _str="Card is already assigned to another user" />;
      case ApiErrorCodesEnum.SESSION_ALREADY_CLOSED:
        return <T _str="Register has already been closed" />;
      case ApiErrorCodesEnum.SESSION_NOT_ON_TILL:
        return <T _str="Register does not belong to correct till" />;
    }
  }

  if (fallback) {
    return fallback;
  }

  return <T _str="Something went wrong" />;
};
